import { Link } from 'gatsby';
import { useI18next, useTranslation } from 'gatsby-plugin-react-i18next';
import React, { FC, ReactNode } from 'react';
import { useSortableData } from '../../../../hooks/useSortableData';
import {
    SecondHandMachinesAccordionTableContainer,
    SecondHandMachinesAccordionTableBox,
    SecondHandMachinesAccordionTableHeader,
    SecondHandMachinesAccordionTableItem,
    SecondHandMachinesAccordionTableRow,
    HeaderItemContent,
    HeaderItemText,
    HeaderItemIcon,
} from './SecondHandMachinesAccordionTableStyle';
import { TiArrowSortedUp, TiArrowSortedDown } from 'react-icons/ti';
import {
    DE,
    DE_AT,
    EN,
    EN_AU,
    EN_IN,
    EN_SG,
    EN_UK,
    ES,
    ES_ES,
    FR,
    FR_BE,
    FR_FR,
} from '../../../../constants/languageCode';
import {
    SHM_DETAIL_PAGE_DE_LINK,
    SHM_DETAIL_PAGE_EN_LINK,
    SHM_DETAIL_PAGE_ES_LINK,
    SHM_DETAIL_PAGE_FR_LINK,
    SORT_KEY_MOTOR,
    SORT_KEY_OUTPUT,
    SORT_KEY_PRESSURE,
    SORT_KEY_SALES_PRICE,
    SORT_KEY_SERIES,
    TABLE_HEADER_ITEM_SHM_CATEGORY,
    TABLE_HEADER_ITEM_SHM_MOTOR,
    TABLE_HEADER_ITEM_SHM_OUTPUT,
    TABLE_HEADER_ITEM_SHM_PRESSURE,
    TABLE_HEADER_ITEM_SHM_PRICE,
    TABLE_HEADER_ITEM_SHM_TYPE,
} from '../../../../constants/secondHandMachinesTypeNames';

interface SecondHandMachinesAccordionTableProps {
    tableData?: any;
    unSortableTableData?: any;
}

const SecondHandMachinesAccordionTable: FC<SecondHandMachinesAccordionTableProps> = ({
    tableData,
    unSortableTableData,
}) => {
    const { t } = useTranslation();
    const { language } = useI18next();
    const tableHeaderItems = [
        TABLE_HEADER_ITEM_SHM_OUTPUT,
        TABLE_HEADER_ITEM_SHM_MOTOR,
        TABLE_HEADER_ITEM_SHM_PRESSURE,
        TABLE_HEADER_ITEM_SHM_TYPE,
        TABLE_HEADER_ITEM_SHM_PRICE,
    ];
    const staticTableHeaderItems = [
        TABLE_HEADER_ITEM_SHM_CATEGORY,
        TABLE_HEADER_ITEM_SHM_OUTPUT,
        TABLE_HEADER_ITEM_SHM_MOTOR,
        TABLE_HEADER_ITEM_SHM_PRESSURE,
        TABLE_HEADER_ITEM_SHM_TYPE,
    ];
    const sortKeys = [SORT_KEY_OUTPUT, SORT_KEY_MOTOR, SORT_KEY_PRESSURE, SORT_KEY_SERIES, SORT_KEY_SALES_PRICE];
    const ASC_SORT = 'ascending';
    const returnSpecificUrlAlias = (language: string) => {
        switch (language) {
            case DE:
                return SHM_DETAIL_PAGE_DE_LINK;
            case DE_AT:
                return SHM_DETAIL_PAGE_DE_LINK;
            case EN:
                return SHM_DETAIL_PAGE_EN_LINK;
            case FR:
                return SHM_DETAIL_PAGE_FR_LINK;
            case ES:
                return SHM_DETAIL_PAGE_ES_LINK;
            case ES_ES:
                return SHM_DETAIL_PAGE_ES_LINK;
            case FR_FR:
                return SHM_DETAIL_PAGE_FR_LINK;
            case FR_BE:
                return SHM_DETAIL_PAGE_FR_LINK;
            case EN_AU:
                return SHM_DETAIL_PAGE_EN_LINK;
            case EN_IN:
                return SHM_DETAIL_PAGE_EN_LINK;
            case EN_UK:
                return SHM_DETAIL_PAGE_EN_LINK;
            case EN_SG:
                return SHM_DETAIL_PAGE_EN_LINK;
            default:
                return SHM_DETAIL_PAGE_DE_LINK;
        }
    };

    const convertTableData = (data: Array<any>): Array<any> =>
        Object.values(data)?.map((item) => ({
            unid: item?.unid,
            Output: parseFloat(item?.Output),
            Motor: parseFloat(item?.Motor),
            Pressure: parseFloat(item?.Pressure),
            Series: item?.Series,
            'Sales Price': parseFloat(item['Sales Price']),
        }));

    const { items, requestSort, sortConfig } = useSortableData(convertTableData(tableData ?? [{}]));

    const createStaticTableHeader = (): ReactNode => (
        <SecondHandMachinesAccordionTableRow>
            {staticTableHeaderItems.map((item: string, index: number) => (
                <SecondHandMachinesAccordionTableHeader key={`${item}-${index}`}>
                    <HeaderItemContent>
                        <HeaderItemText>{t(item)}</HeaderItemText>
                    </HeaderItemContent>
                </SecondHandMachinesAccordionTableHeader>
            ))}
        </SecondHandMachinesAccordionTableRow>
    );

    const createTableHeader = (): ReactNode => (
        <SecondHandMachinesAccordionTableRow>
            {tableHeaderItems.map((item: string, index: number) => (
                <SecondHandMachinesAccordionTableHeader
                    key={item + index}
                    onClick={() => {
                        requestSort(sortKeys[index]);
                    }}
                >
                    <HeaderItemContent>
                        <HeaderItemText>{t(item)}</HeaderItemText>
                        <HeaderItemIcon>
                            {sortConfig?.key === sortKeys[index] && sortConfig?.direction === ASC_SORT ? (
                                <TiArrowSortedUp />
                            ) : (
                                <TiArrowSortedDown />
                            )}
                        </HeaderItemIcon>
                    </HeaderItemContent>
                </SecondHandMachinesAccordionTableHeader>
            ))}
        </SecondHandMachinesAccordionTableRow>
    );

    const convertPriceFormat = (item: number): string =>
        language === DE
            ? `${item?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},00€`
            : `${item?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.00€`;

    const createTableContent = (): ReactNode =>
        items?.map((item: any) => (
            <SecondHandMachinesAccordionTableRow key={item?.unid}>
                <SecondHandMachinesAccordionTableItem>
                    {!isNaN(item?.Output) && item?.Output}
                </SecondHandMachinesAccordionTableItem>
                <SecondHandMachinesAccordionTableItem>
                    {!isNaN(item?.Motor) && item?.Motor}
                </SecondHandMachinesAccordionTableItem>
                <SecondHandMachinesAccordionTableItem>
                    {!isNaN(item?.Pressure) && item?.Pressure}
                </SecondHandMachinesAccordionTableItem>
                <SecondHandMachinesAccordionTableItem>
                    <Link to={`/${language}${returnSpecificUrlAlias(language)}${item?.unid}`}>{item?.Series}</Link>
                </SecondHandMachinesAccordionTableItem>
                <SecondHandMachinesAccordionTableItem isPrice={true}>
                    {!isNaN(item['Sales Price']) && convertPriceFormat(item['Sales Price'])}
                </SecondHandMachinesAccordionTableItem>
            </SecondHandMachinesAccordionTableRow>
        ));

    const createStaticTableContent = (): ReactNode =>
        unSortableTableData?.map((item: any) => (
            <SecondHandMachinesAccordionTableRow key={item?.unid}>
                <SecondHandMachinesAccordionTableItem>{item?.Type}</SecondHandMachinesAccordionTableItem>
                <SecondHandMachinesAccordionTableItem>{item?.Output}</SecondHandMachinesAccordionTableItem>
                <SecondHandMachinesAccordionTableItem>{item?.Motor}</SecondHandMachinesAccordionTableItem>
                <SecondHandMachinesAccordionTableItem>{item?.Pressure}</SecondHandMachinesAccordionTableItem>
                <SecondHandMachinesAccordionTableItem>{item?.Series}</SecondHandMachinesAccordionTableItem>
            </SecondHandMachinesAccordionTableRow>
        ));

    return (
        <SecondHandMachinesAccordionTableContainer>
            <SecondHandMachinesAccordionTableBox>
                {tableData ? <thead>{createTableHeader()}</thead> : <thead>{createStaticTableHeader()}</thead>}
                {tableData ? <tbody>{createTableContent()}</tbody> : <tbody>{createStaticTableContent()}</tbody>}
            </SecondHandMachinesAccordionTableBox>
        </SecondHandMachinesAccordionTableContainer>
    );
};

export default SecondHandMachinesAccordionTable;
